<template>
  <section class="hero-section" :dir="isRTL ? 'rtl' : 'ltr'" :class="{ 'is-ltr': !isRTL }">

    <!-- Blue polygon — clip-path with rounded corners (no SVG) -->
    <div class="blue-polygon"></div>

    <!-- Orange polygon — clip-path with rounded corners (no SVG) -->
    <div class="orange-polygon"></div>

    <!-- Logo — centered top -->
    <div class="logo-wrap">
      <a href="http://monglish.co.uk" target="_blank" rel="noopener">
        <img :src="monglishLogo" :alt="t('hero.logo_alt')" class="logo" />
      </a>
    </div>

    <!-- Headline — ABOVE the blue polygon, black with orange-highlighted number -->
    <h1 class="main-headline">
      <span>{{ t('hero.headline_part1') }}</span><span class="hl">{{ t('hero.headline_highlight') }}</span><span>{{ t('hero.headline_part2') }}</span>
    </h1>

    <!-- Blue polygon group: wraps sub-heading + image so they share the blue
         background on mobile. On desktop, this wrapper is display:contents
         and the children continue to position absolutely. -->
    <div class="hero-blue-group">
      <h3 class="sub-heading">{{ t('hero.subheadline') }}</h3>
      <div class="hero-image">
        <img :src="heroKids" alt="Kids learning English" class="kids-img" />
      </div>
    </div>

    <!-- CTA — sits inside blue right block on desktop, at very bottom on mobile -->
    <CtaButton class="hero-cta" :title="t('hero.cta_title')" :subtitle="t('hero.cta_subtitle')" />

    <!-- Description — h5, INSIDE orange right block -->
    <h5 class="hero-desc">{{ t('hero.info_card_left') }}</h5>

    <!-- Stats bar — bottom of orange polygon -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-value">{{ t('hero.stat1_value') }}</span>
        <span class="stat-label">{{ t('hero.stat1_label') }}</span>
      </div>
      <div class="stat-sep"></div>
      <div class="stat-item">
        <span class="stat-value">{{ t('hero.stat2_value') }}</span>
        <span class="stat-label">{{ t('hero.stat2_label') }}</span>
      </div>
      <div class="stat-sep"></div>
      <div class="stat-item">
        <span class="stat-value">{{ t('hero.stat3_value') }}</span>
        <span class="stat-label">{{ t('hero.stat3_label') }}</span>
      </div>
    </div>

  </section>
</template>

<script setup>
import CtaButton from '../ui/CtaButton.vue'
import { onMounted } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import heroKids from '@/assets/hero-kids.png'
import monglishLogo from '@/assets/monglish-logo.svg'

const { t, isRTL, initializeLanguage } = useLanguage()

onMounted(() => {
  initializeLanguage()
})
</script>

<style scoped>
/* Hero locks Figma's 1920:1012 ratio so all clip-path % values stay accurate.
   Width and margin match .content-section-container exactly. */
.hero-section {
  position: relative;
  width: calc(100% - var(--spacing-lg));
  max-width: 1450px;
  margin: var(--spacing-2xl) auto;
  aspect-ratio: 1920 / 1012;
  background: #ffffff;
  overflow: hidden;
}

/* ── Blue polygon ─────────────────────────────────────────
   24-point clip-path: 8 corner regions, each approximated with
   3 points (start, mid, end) to soften every corner.
   Coordinates derived from Figma SVG path bezier midpoints. */
.blue-polygon {
  position: absolute;
  inset: 0;
  background: #0F6396;
  /* x-coords stretched from Figma's 3.59%–95.31% range to 0%–100% so the polygon
     reaches the section edges and visually matches the content section below. */
  clip-path: polygon(
    /* corner 1: top-left of left block */
    1.33% 19.57%, 0.81% 19.74%, 0.39% 20.24%, 0.11% 20.97%, 0% 21.87%,
    /* corner 2: bottom-left of left block */
    0% 75.17%, 0.11% 76.07%, 0.39% 76.80%, 0.81% 77.29%, 1.33% 77.47%,
    /* corner 3: bottom-right of left block — bigger round (radius 2 along segments) */
    42.04% 77.47%, 43.21% 77.43%, 43.93% 77.25%, 44.45% 76.74%, 45.07% 75.76%,
    /* corner 4: top of bottom diagonal — bigger round */
    50.07% 67.42%, 50.69% 66.44%, 51.22% 65.92%, 51.92% 65.73%, 53.10% 65.71%,
    /* corner 5: bottom-right of right block */
    98.69% 65.71%, 99.21% 65.53%, 99.62% 65.04%, 99.91% 64.31%, 100% 63.41%,
    /* corner 6: top-right of right block */
    100% 31.85%, 99.91% 30.95%, 99.62% 30.22%, 99.21% 29.72%, 98.69% 29.55%,
    /* corner 7: top-left of right block — bigger round */
    46.94% 29.55%, 45.78% 29.52%, 45.06% 29.33%, 44.55% 28.82%, 43.94% 27.82%,
    /* corner 8: top of top diagonal — bigger round */
    40.20% 21.30%, 39.59% 20.31%, 39.08% 19.79%, 38.37% 19.60%, 37.20% 19.57%
  );
  z-index: 1;
}

/* ── Orange polygon ───────────────────────────────────────
   14-point clip-path: 4 outer corners rounded with 3 points each. */
.orange-polygon {
  position: absolute;
  inset: 0;
  background: radial-gradient(144.33% 603% at 109.98% 122.06%, #E0701A 0%, #FF9F56 66.51%, #E0701A 100%);
  /* x-coords stretched to 0%–100% so the polygon reaches the section edges.
     Bottom-strip top moved up to y=81% (was 84%) to reduce gap with blue polygon on the left. */
  clip-path: polygon(
    /* corner: top-right of bottom strip — bigger smooth round into diagonal */
    41.45% 81%, 42.62% 80.97%, 43.34% 80.80%, 43.90% 80.31%, 44.60% 79.37%,
    /* diagonal up-right */
    /* corner: top-left of right block — bigger smooth round out of diagonal */
    50.48% 71.03%, 51.18% 70.08%, 51.73% 69.61%, 52.45% 69.41%, 53.63% 69.4%,
    /* top edge of right block */
    98.65% 69.4%,
    /* top-right large round */
    99.18% 69.54%, 99.61% 70.02%, 99.91% 70.74%, 100% 71.64%,
    /* right edge */
    100% 97.73%,
    /* bottom-right large round */
    99.91% 98.62%, 99.61% 99.35%, 99.18% 99.82%, 98.65% 100%,
    /* bottom edge */
    1.31% 100%,
    /* bottom-left large round */
    0.80% 99.82%, 0.38% 99.35%, 0.11% 98.62%, 0% 97.73%,
    /* left edge up */
    0% 83.27%,
    /* top-left of bottom strip large round */
    0.11% 82.37%, 0.38% 81.65%, 0.80% 81.17%, 1.31% 81%
  );
  z-index: 2;
}

/* ── Logo (top center) ──────────────────────────────────── */
.logo-wrap {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.logo {
  height: clamp(28px, 3vw, 56px);
  width: auto;
  display: block;
}

/* ── Headline (ABOVE blue polygon, BLACK text) ───────────
   Wider container + smaller font so headline wraps to 2 lines like Figma. */
.main-headline {
  position: absolute;
  top: 11%;
  right: 4%;
  width: 53%;
  margin: 0;
  z-index: 6;
  font-size: clamp(16px, 1.8vw, 30px);
  font-weight: 800;
  color: #0a0a0a;
  line-height: 1.5;
  font-family: 'Cairo', sans-serif;
  text-align: right;
}

.main-headline .hl {
  color: #E8821A;
}

/* Wrapper that groups sub-heading + image — invisible on desktop, becomes
   the blue rounded "polygon" on mobile. */
.hero-blue-group {
  display: contents;
}

/* Sub-heading sits inside the blue right block on desktop (positioned absolute) */
.sub-heading {
  position: absolute;
  top: 33%;
  right: 6%;
  width: 47%;
  margin: 0;
  z-index: 6;
  font-size: clamp(20px, 2.4vw, 38px);
  font-weight: 700;
  color: #ffffff;
  line-height: 1.5;
  font-family: 'Cairo', sans-serif;
  text-align: right;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
}

/* CTA sits below sub-heading inside the blue right block on desktop */
.hero-cta {
  position: absolute;
  top: 50%;
  right: 6%;
  width: 47%;
  max-width: 540px;
  z-index: 6;
}

.hero-cta :deep(.button-title) {
  font-size: clamp(16px, 1.5vw, 24px);
}

.hero-cta :deep(.button-subtitle) {
  font-size: clamp(11px, 1vw, 15px);
}

/* ── Kids image — extends OUTSIDE the blue polygon
   so heads stick up above and the orange curl shows on the left */
.hero-image {
  position: absolute;
  top: 4%;
  bottom: 22.5%;
  left: 0%;
  width: 42%;
  z-index: 6;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
  pointer-events: none;
}

.kids-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: bottom center;
  display: block;
}

/* ── Description (INSIDE orange right block) ────────────
   Orange right block: y=69.4% → 84%, x ≈ 51% → 95% */
.hero-desc {
  position: absolute;
  top: 75%;
  right: 8%;
  width: 28%;
  margin: 0;
  z-index: 6;
  font-size: clamp(10px, 0.95vw, 14px);
  font-weight: 400;
  color: #ffffff;
  line-height: 1.8;
  font-family: 'Cairo', sans-serif;
  text-align: right;
}

/* ── Stats bar (bottom of orange) ─────────────────────── */
.stats-bar {
  position: absolute;
  top: 86.5%;
  bottom: 4%;
  left: 6%;
  right: 6%;
  z-index: 6;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: clamp(20px, 4vw, 70px);
  direction: ltr;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-value {
  font-size: clamp(16px, 2vw, 32px);
  font-weight: 800;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  line-height: 1;
}

.stat-label {
  font-size: clamp(10px, 0.95vw, 14px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;
}

.stat-sep {
  width: 1px;
  height: clamp(26px, 2.8vw, 46px);
  background: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

/* ── LTR overrides (English) — keep block positions, only change text font/align/size ── */
.hero-section.is-ltr .main-headline,
.hero-section.is-ltr .sub-heading,
.hero-section.is-ltr .hero-desc {
  font-family: 'Poppins', sans-serif;
  text-align: left;
}

.hero-section.is-ltr .main-headline {
  font-size: clamp(13px, 1.35vw, 22px);
  line-height: 1.35;
  font-weight: 700;
}

.hero-section.is-ltr .sub-heading {
  font-size: clamp(15px, 1.7vw, 26px);
  line-height: 1.35;
}

.hero-section.is-ltr .hero-desc {
  font-size: clamp(11px, 1vw, 15px);
  line-height: 1.5;
}

.hero-section.is-ltr .hero-cta {
  right: auto;
  left: 47%;
  width: auto;
  max-width: 47%;
}

.hero-section.is-ltr .hero-cta :deep(.button-title) {
  font-size: clamp(12px, 1.1vw, 18px);
}

.hero-section.is-ltr .hero-cta :deep(.button-subtitle) {
  font-size: clamp(9px, 0.75vw, 12px);
}

/* ── Mobile (≤768px) ────────────────────────────────────
   Portrait layout: stacked vertically with .hero-blue-group acting as
   the blue "polygon" wrapping sub-heading + kids image (which overflows
   the bottom). Desktop Z-polygons are hidden. */
@media (max-width: 768px) {
  .hero-section {
    aspect-ratio: auto;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }

  .blue-polygon,
  .orange-polygon { display: none; }

  /* Reset all positioning so elements flow in flex column */
  .logo-wrap,
  .main-headline,
  .hero-blue-group,
  .sub-heading,
  .hero-image,
  .hero-cta,
  .stats-bar,
  .hero-desc {
    position: relative;
    inset: auto;
    top: auto;
    bottom: auto;
    left: auto;
    right: auto;
    width: auto;
    transform: none;
  }

  .logo-wrap {
    order: 1;
    padding: 4px 0 8px;
    text-align: center;
  }
  .logo-wrap a { display: inline-block; }
  .logo { height: clamp(40px, 12vw, 60px); }

  .main-headline {
    order: 2;
    text-align: center;
    padding: 0 8px;
    font-size: clamp(16px, 4.6vw, 22px);
  }

  /* Blue "polygon" wrapping sub-heading + image (image overflows bottom) */
  .hero-blue-group {
    display: flex;
    flex-direction: column;
    order: 3;
    background: #0F6396;
    border-radius: 24px;
    padding: 24px 18px 0;
    overflow: visible;
  }

  .sub-heading {
    background: transparent;
    color: #ffffff;
    text-shadow: none;
    text-align: center;
    font-size: clamp(15px, 4vw, 20px);
    line-height: 1.5;
    padding: 0 8px;
    margin: 0 0 16px;
  }

  /* Kids image inside the blue group — bottom extends past the group */
  .hero-image {
    height: clamp(240px, 60vw, 340px);
    align-items: flex-end;
    justify-content: center;
    overflow: visible;
    margin-bottom: -32px; /* image bottom extends below blue group */
  }
  .kids-img {
    height: 110%;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    object-position: bottom center;
  }

  /* Stats bar on white between blue group and orange polygon */
  .stats-bar {
    order: 4;
    background: #ffffff;
    justify-content: center;
    padding: 12px 8px;
    gap: clamp(16px, 5vw, 28px);
  }
  .stat-value { color: #0F6396; }
  .stat-label { color: #555; }
  .stat-sep {
    background: rgba(0, 0, 0, 0.18);
    height: clamp(28px, 8vw, 36px);
  }

  /* Orange rounded "polygon" — wraps the description */
  .hero-desc {
    order: 5;
    background: radial-gradient(144.33% 603% at -9.98% 122.06%, #E0701A 0%, #FF9F56 66.51%, #E0701A 100%);
    border-radius: 24px;
    padding: 24px 18px;
    text-align: center;
    color: #ffffff;
    font-size: clamp(11px, 3.4vw, 14px);
    line-height: 1.7;
  }

  /* CTA at the very bottom */
  .hero-cta {
    order: 6;
    max-width: 100%;
  }
}
</style>
